import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCategories, fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal } from "lucide-react";

export default function Shop() {
  const { category } = useParams();
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => setCats([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProducts({ category })
      .then((data) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category]);

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "low") arr.sort((a, b) => a.price - b.price);
    if (sort === "high") arr.sort((a, b) => b.price - a.price);
    if (sort === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [products, sort]);

  const currentCat = cats.find((c) => c.slug === category);
  const title = currentCat ? currentCat.name : "All Products";

  return (
    <div data-testid="shop-page">
      {/* Header */}
      <section className="border-b-[3px] border-black bg-[var(--secondary)] candy-dots">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="nb-pill inline-block mb-4">SHOP</div>
          <h1 className="font-display font-black uppercase text-5xl md:text-7xl tracking-tight">{title}</h1>
          <p className="mt-3 text-lg font-medium max-w-2xl">Tap any product → buy on WhatsApp directly. We ship locally and across India.</p>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-wrap gap-3 items-center mb-6">
          <Link
            to="/shop"
            data-testid="filter-all"
            className={`nb-pill ${!category ? "bg-[var(--primary)] text-white" : ""}`}
          >
            All
          </Link>
          {cats.map((c) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              data-testid={`filter-${c.slug}`}
              className={`nb-pill ${category === c.slug ? "bg-[var(--primary)] text-white" : ""}`}
            >
              {c.name}
            </Link>
          ))}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div className="text-sm font-bold uppercase tracking-wider text-black/70">
            {loading ? "Loading…" : `${sorted.length} products`}
          </div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={18} strokeWidth={2.5} />
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="nb-pill !py-2 !pr-8 cursor-pointer outline-none"
            >
              <option value="featured">Featured</option>
              <option value="low">Price: Low → High</option>
              <option value="high">Price: High → Low</option>
              <option value="name">Name (A-Z)</option>
            </select>
          </div>
        </div>

        {sorted.length === 0 && !loading ? (
          <div className="nb-card p-10 text-center">
            <h3 className="font-display font-black text-2xl mb-2">No products yet</h3>
            <p>Check back soon — fresh drops every week.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {sorted.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
